package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import co.maytes.standupbot.shared.template.SectionKey
import co.maytes.standupbot.shared.template.SectionsConfigDto
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.attributes.alt
import org.jetbrains.compose.web.attributes.src
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Hr
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

enum class SectionStatus { Generated, Edited, Approved, SkippedToday }

data class StandupUpdate(
    val person: String,
    val yesterday: String,
    val today: String,
    val blockers: String? = null,
    val status: SectionStatus = SectionStatus.Generated,
    val acknowledgedBadge: String? = null,
    val userId: String? = null,
)

@Composable
fun SlackMessagePreview(
    channel: String = "standup",
    timestamp: String = "9:03 AM",
    dateLabel: String = "Monday, Apr 22",
    updates: List<StandupUpdate> = defaultUpdates,
    layout: SectionsConfigDto = SectionsConfigDto.DEFAULT,
    onLeave: List<String> = emptyList(),
    floating: Boolean = false,
    ackPressed: Boolean = false,
    modifier: Modifier = Modifier,
    onAck: (() -> Unit)? = null,
    onEdit: (() -> Unit)? = null,
    onSkip: (() -> Unit)? = null,
    onRevert: (() -> Unit)? = null,
) {
    Div(
        attrs = Modifier
            .classNames("sb-slack")
            .fillMaxWidth()
            .let { if (floating) it.classNames("sb-slack", "sb-float") else it }
            .then(modifier)
            .toAttrs(),
    ) {
        SlackChannelStrip(channel)
        Div(attrs = Modifier.classNames("sb-slack-row").toAttrs()) {
            SlackAvatar()
            Div(attrs = Modifier.classNames("sb-slack-content").toAttrs()) {
                SlackHeadline(timestamp)
                SlackSubject(dateLabel)
                updates.forEach { SlackPersonBlock(it, layout) }
                if (onLeave.isNotEmpty()) SlackOnLeaveLine(onLeave)
                Hr(attrs = Modifier.classNames("sb-slack-divider").toAttrs())
                SlackActionRow(ackPressed = ackPressed, onAck = onAck, onEdit = onEdit, onSkip = onSkip, onRevert = onRevert)
            }
        }
    }
}

@Composable
private fun SlackChannelStrip(channel: String) {
    Div(attrs = Modifier.classNames("sb-slack-channel").toAttrs()) {
        Span(attrs = Modifier.classNames("sb-slack-channel-hash").toAttrs()) { Text("#") }
        Text(channel)
    }
}

@Composable
private fun SlackAvatar() {
    Div(attrs = Modifier.classNames("sb-slack-avatar").toAttrs()) {
        Img(
            src = "/logo-square.svg",
            attrs = {
                alt("StandupBot")
            },
        )
    }
}

@Composable
private fun SlackHeadline(timestamp: String) {
    Div(attrs = Modifier.classNames("sb-slack-headline").toAttrs()) {
        Span(attrs = Modifier.classNames("sb-slack-author").toAttrs()) { Text("StandupBot") }
        Span(attrs = Modifier.classNames("sb-slack-app-badge").toAttrs()) { Text("APP") }
        Span(attrs = Modifier.classNames("sb-slack-time").toAttrs()) { Text(timestamp) }
    }
}

@Composable
private fun SlackSubject(dateLabel: String) {
    Div(attrs = Modifier.classNames("sb-slack-subject").toAttrs()) {
        Text("📋  Daily standup · $dateLabel")
    }
}

@Composable
private fun SlackPersonBlock(
    update: StandupUpdate,
    layout: SectionsConfigDto,
) {
    if (update.status == SectionStatus.SkippedToday) {
        Div(attrs = Modifier.classNames("sb-slack-person").toAttrs()) {
            Span(attrs = Modifier.classNames("sb-slack-person-name").toAttrs()) {
                Text("@${update.person}")
            }
            Span(attrs = Modifier.classNames("sb-slack-badge-muted").toAttrs()) {
                Text(" — skipped today")
            }
        }
        return
    }

    Div(attrs = Modifier.classNames("sb-slack-person").toAttrs()) {
        Span(attrs = Modifier.classNames("sb-slack-person-name").toAttrs()) {
            Text("@${update.person}")
        }
        when {
            update.acknowledgedBadge != null ->
                Span(attrs = Modifier.classNames("sb-slack-badge-muted").toAttrs()) {
                    Text("  ${update.acknowledgedBadge}")
                }
            update.status == SectionStatus.Edited ->
                Span(attrs = Modifier.classNames("sb-slack-badge-muted").toAttrs()) {
                    Text("  ✎ edited")
                }
        }
    }
    layout.includedSlots().forEach { slot ->
        val text = update.textFor(slot.key) ?: return@forEach
        val prefix = if (slot.key == SectionKey.BLOCKERS) "🚧 " else ""
        Div(attrs = Modifier.classNames("sb-slack-line").toAttrs()) {
            Span(attrs = Modifier.classNames("sb-slack-line-label").toAttrs()) { Text("$prefix${slot.label}: ") }
            SlackMrkdwnText(text)
        }
    }
}

private fun StandupUpdate.textFor(key: SectionKey): String? =
    when (key) {
        SectionKey.YESTERDAY -> yesterday
        SectionKey.TODAY -> today
        SectionKey.BLOCKERS -> blockers
    }?.takeIf { it.isNotEmpty() }

@Composable
private fun SlackOnLeaveLine(people: List<String>) {
    Div(attrs = Modifier.classNames("sb-slack-line").toAttrs()) {
        Span(attrs = Modifier.classNames("sb-slack-line-label").toAttrs()) { Text("🌴 On leave: ") }
        Text(people.joinToString(" · "))
    }
}

@Composable
private fun SlackActionRow(
    ackPressed: Boolean = false,
    onAck: (() -> Unit)? = null,
    onEdit: (() -> Unit)? = null,
    onSkip: (() -> Unit)? = null,
    onRevert: (() -> Unit)? = null,
) {
    Div(attrs = Modifier.classNames("sb-slack-actions").toAttrs()) {
        Div(attrs = Modifier.classNames("sb-slack-btn", "sb-slack-btn--primary")
            .let { if (ackPressed) it.classNames("sb-slack-btn--pressed") else it }
            .let { if (onAck != null) it.onClick { onAck() }.cursor(Cursor.Pointer) else it }
            .toAttrs()) { Text("Looks good") }
        Div(attrs = Modifier.classNames("sb-slack-btn")
            .let { if (onEdit != null) it.onClick { onEdit() }.cursor(Cursor.Pointer) else it }
            .toAttrs()) { Text("Edit mine") }
        Div(attrs = Modifier.classNames("sb-slack-btn")
            .let { if (onSkip != null) it.onClick { onSkip() }.cursor(Cursor.Pointer) else it }
            .toAttrs()) { Text("Skip today") }
        if (onRevert != null) {
            Div(attrs = Modifier.classNames("sb-slack-btn")
                .onClick { onRevert() }.cursor(Cursor.Pointer)
                .toAttrs()) { Text("Revert to AI") }
        }
    }
}

private val defaultUpdates: List<StandupUpdate> = listOf(
    StandupUpdate(
        person = "maya",
        yesterday = "Shipped OAuth refresh-token rotation (STB-421). Reviewed 3 PRs on the scheduler.",
        today = "Wiring Quartz cron UI into standup settings — pairing with Sam at 11.",
        blockers = "Waiting on design for the empty state. Nudged Priya.",
    ),
    StandupUpdate(
        person = "jordan",
        yesterday = "Landed Linear webhook dedupe (STB-398). Closed 2 flaky scheduler tests.",
        today = "Rolling out the activity-feed index behind a feature flag.",
    ),
    StandupUpdate(
        person = "priya",
        yesterday = "Finalized dashboard onboarding wireframes. Sign-off from Max.",
        today = "Prototyping the schedule-builder drag interaction in Figma.",
    ),
)
